import React, { useState } from 'react';
import {
  Box,
  Text,
  Input,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  InputGroup,
  InputLeftAddon,
  Select,
} from '@chakra-ui/react';
import { useColors } from './colors';

const MarketplaceListingInfo = ({ listing, onUpdate }) => {
  const {
    bgColor,
    borderColor,
    headingColor,
    subTextColor: labelColor,
    textColor,
    inputColor,
    placeholderColor,
    hoverBg: selectHoverBg,
    previewBg: selectFocusBg,
  } = useColors();

  // Read-only specific colors
  const readOnlyBg = selectHoverBg;
  const readOnlyColor = inputColor;

  // Select styling colors
  const selectOptionBg = bgColor;

  const isEbay = listing?.marketplace?.name?.toLowerCase() === 'ebay';

  // Initialize package weight from API data
  const initPackageWeight = () => {
    if (isEbay && listing?.marketplaceDetails?.packageWeight) {
      return {
        lbs: listing.marketplaceDetails.packageWeight.weightMajor?.value || '',
        oz: listing.marketplaceDetails.packageWeight.weightMinor?.value || '',
      };
    } else if (listing?.marketplaceDetails?.packageWeight) {
      return {
        value: listing.marketplaceDetails.packageWeight.value || '',
        unit: listing.marketplaceDetails.packageWeight.unit || 'pounds',
      };
    }
    return { lbs: '', oz: '', value: '', unit: 'pounds' };
  };

  const initPackageDimensions = () => {
    if (listing?.marketplaceDetails?.packageDimensions) {
      const dims = listing.marketplaceDetails.packageDimensions;
      return {
        length: dims.length?.value || '',
        width: dims.width?.value || '',
        height: isEbay ? dims.depth?.value || '' : dims.height?.value || '',
        unit: dims.unit || 'inches',
      };
    }
    return { length: '', width: '', height: '', unit: 'inches' };
  };

  // Local state for editable fields
  const initialWeight = initPackageWeight();
  const [packageWeight, setPackageWeight] = useState(initialWeight.value || '');
  const [packageWeightUnit, setPackageWeightUnit] = useState(
    initialWeight.unit || 'pounds',
  );

  // eBay specific weight states
  const [packageWeightLbs, setPackageWeightLbs] = useState(initialWeight.lbs || '');
  const [packageWeightOz, setPackageWeightOz] = useState(initialWeight.oz || '');

  const [packageDimensions, setPackageDimensions] = useState(initPackageDimensions());

  const handlePackageWeightChange = (e) => {
    const newWeight = e.target.value;
    setPackageWeight(newWeight);

    // Format for Amazon listings
    onUpdate?.({
      packageWeight: {
        value: newWeight,
        unit: packageWeightUnit,
      },
    });
  };

  const handlePackageWeightUnitChange = (e) => {
    const newUnit = e.target.value;
    setPackageWeightUnit(newUnit);

    // Format for Amazon listings
    onUpdate?.({
      packageWeight: {
        value: packageWeight,
        unit: newUnit,
      },
    });
  };

  const handlePackageWeightLbsChange = (e) => {
    const newWeightLbs = e.target.value;
    setPackageWeightLbs(newWeightLbs);

    // Format for eBay listings
    onUpdate?.({
      packageWeight: {
        weightMajor: { value: newWeightLbs, unit: 'lbs' },
        weightMinor: { value: packageWeightOz, unit: 'oz' },
      },
    });
  };

  const handlePackageWeightOzChange = (e) => {
    const newWeightOz = e.target.value;
    setPackageWeightOz(newWeightOz);

    // Format for eBay listings
    onUpdate?.({
      packageWeight: {
        weightMajor: { value: packageWeightLbs, unit: 'lbs' },
        weightMinor: { value: newWeightOz, unit: 'oz' },
      },
    });
  };

  const handlePackageDimensionChange = (field, value) => {
    const newDimensions = { ...packageDimensions, [field]: value };
    setPackageDimensions(newDimensions);

    // Format dimensions properly based on marketplace
    const formattedDimensions = {};

    if (isEbay) {
      // For eBay, map height to depth
      formattedDimensions.packageDimensions = {
        length: { value: newDimensions.length, unit: newDimensions.unit },
        width: { value: newDimensions.width, unit: newDimensions.unit },
        depth: { value: newDimensions.height, unit: newDimensions.unit }, // height becomes depth for eBay
        unit: newDimensions.unit,
      };
    } else {
      // For other marketplaces
      formattedDimensions.packageDimensions = {
        length: { value: newDimensions.length, unit: newDimensions.unit },
        width: { value: newDimensions.width, unit: newDimensions.unit },
        height: { value: newDimensions.height, unit: newDimensions.unit },
        unit: newDimensions.unit,
      };
    }

    onUpdate?.(formattedDimensions);
  };

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      p={6}
      border="1px solid"
      borderColor={borderColor}
      w="100%"
    >
      <Text fontSize="xl" fontWeight="semibold" color={headingColor} mb={6}>
        Marketplace Listing Info
      </Text>

      <VStack spacing={5} align="stretch">
        {/* Category/Product Type */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            {isEbay ? 'Category' : 'Product Type'}
          </FormLabel>
          <Input
            value={
              isEbay
                ? listing?.marketplaceDetails?.category || ''
                : listing?.marketplaceDetails?.productType || ''
            }
            isReadOnly
            bg={readOnlyBg}
            color={readOnlyColor}
            size="md"
            borderRadius="md"
            fontSize="sm"
            placeholder="--"
          />
        </FormControl>

        {/* eBay specific fields */}
        {isEbay && (
          <>
            <HStack spacing={5} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
                  Compatibility Count
                </FormLabel>
                <Input
                  value={listing?.marketplaceDetails?.compatibilityCount || ''}
                  isReadOnly
                  bg={readOnlyBg}
                  color={readOnlyColor}
                  size="md"
                  borderRadius="md"
                  placeholder="--"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
                  Item Location
                </FormLabel>
                <Input
                  value={listing?.marketplaceDetails?.itemLocation || ''}
                  isReadOnly
                  bg={readOnlyBg}
                  color={readOnlyColor}
                  size="md"
                  borderRadius="md"
                  placeholder="--"
                />
              </FormControl>
            </HStack>
          </>
        )}

        <HStack spacing={2}>
          {/* Package Weight */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Package Weight
            </FormLabel>
            {isEbay ? (
              // eBay: Two separate input fields with left addons
              <HStack spacing={2} w="50%">
                <InputGroup size="md" w="50%">
                  <InputLeftAddon children="lbs" />
                  <Input
                    value={packageWeightLbs}
                    onChange={handlePackageWeightLbsChange}
                    placeholder="0.0"
                    type="number"
                    step="0.1"
                    min="0"
                    borderRadius="md"
                    _focus={{
                      borderColor: 'blue.400',
                      boxShadow: '0 0 0 1px #3182ce',
                    }}
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
                <InputGroup size="md" w="50%">
                  <InputLeftAddon children="oz" />
                  <Input
                    value={packageWeightOz}
                    onChange={handlePackageWeightOzChange}
                    placeholder="0.0"
                    type="number"
                    step="0.1"
                    min="0"
                    borderRadius="md"
                    _focus={{
                      borderColor: 'blue.400',
                      boxShadow: '0 0 0 1px #3182ce',
                    }}
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
              </HStack>
            ) : (
              // Amazon: Original input + select dropdown
              <HStack spacing={2} w="50%">
                <Input
                  value={packageWeight}
                  onChange={handlePackageWeightChange}
                  placeholder="0.0"
                  type="number"
                  step="0.1"
                  min="0"
                  size="md"
                  borderRadius="md"
                  _focus={{
                    borderColor: 'blue.400',
                    boxShadow: '0 0 0 1px #3182ce',
                  }}
                  color={inputColor}
                  _placeholder={{ color: placeholderColor }}
                  w="50%"
                />
                <Select
                  value={packageWeightUnit}
                  onChange={handlePackageWeightUnitChange}
                  size="md"
                  w="50%"
                  _hover={{ bg: selectHoverBg }}
                  _focus={{ bg: selectFocusBg }}
                >
                  <option value="pounds" style={{ background: selectOptionBg }}>
                    pounds
                  </option>
                </Select>
              </HStack>
            )}
          </FormControl>
        </HStack>

        {/* Package Dimensions */}
        <FormControl w="50%">
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            Package Dimensions
          </FormLabel>
          <HStack spacing={2}>
            <Input
              value={packageDimensions.length}
              onChange={(e) => handlePackageDimensionChange('length', e.target.value)}
              placeholder="Length"
              type="number"
              step="0.1"
              min="0"
              size="md"
              borderRadius="md"
              _focus={{
                borderColor: 'blue.400',
                boxShadow: '0 0 0 1px #3182ce',
              }}
              color={inputColor}
              _placeholder={{ color: placeholderColor }}
              w="25%"
            />
            <Text color={textColor} fontSize="sm">
              ×
            </Text>
            <Input
              value={packageDimensions.width}
              onChange={(e) => handlePackageDimensionChange('width', e.target.value)}
              placeholder="Width"
              type="number"
              step="0.1"
              min="0"
              size="md"
              borderRadius="md"
              _focus={{
                borderColor: 'blue.400',
                boxShadow: '0 0 0 1px #3182ce',
              }}
              color={inputColor}
              _placeholder={{ color: placeholderColor }}
              w="25%"
            />
            <Text color={textColor} fontSize="sm">
              ×
            </Text>
            <Input
              value={packageDimensions.height}
              onChange={(e) => handlePackageDimensionChange('height', e.target.value)}
              placeholder={isEbay ? 'Depth' : 'Height'}
              type="number"
              step="0.1"
              min="0"
              size="md"
              borderRadius="md"
              _focus={{
                borderColor: 'blue.400',
                boxShadow: '0 0 0 1px #3182ce',
              }}
              color={inputColor}
              _placeholder={{ color: placeholderColor }}
              w="25%"
            />
            <Select
              value={packageDimensions.unit}
              onChange={(e) => handlePackageDimensionChange('unit', e.target.value)}
              size="md"
              w="25%"
              _hover={{ bg: selectHoverBg }}
              _focus={{ bg: selectFocusBg }}
            >
              <option value="inches" style={{ background: selectOptionBg }}>
                inches
              </option>
            </Select>
          </HStack>
        </FormControl>
      </VStack>
    </Box>
  );
};

export default MarketplaceListingInfo;
